import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, MoreVertical } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { sendMessage } from '../../firebase/conversation.service';
import { filesToValidatedFiles } from '../../utils/attachmentHelpers';
import { uploadAttachments } from '../../utils/uploadAttachments';
import { formatMessageTime, getMessageSourceLabel } from '../../utils/inboxHelpers';
import { getRunsForLead, stopRun } from '../../firebase/automationRuns.service';
import { sanitizeHtml, containsHtml } from '../../utils/sanitizeHtml';
import MobileDetailsPanel from './MobileDetailsPanel';

const getMessageContent = (message) => {
    const candidates = [message?.content, message?.body, message?.html, message?.text];
    return candidates.find((value) => typeof value === 'string' && value.length > 0) || '';
};

const shouldRenderHtml = (message, content) => {
    return Boolean(message?.isHtml) || containsHtml(content);
};

const MobileChatView = ({ chat, leadData, messages = [], channel, setChannel, messageInput, setMessageInput, handleSendMessage, onBack, loading = false, loadingDetails = false }) => {
    const [showDetails, setShowDetails] = useState(false);
    const { user } = useAuth();
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [subject, setSubject] = useState('');
    const [pendingFiles, setPendingFiles] = useState([]);
    // Stop Bot state
    const [hasActiveAutomation, setHasActiveAutomation] = useState(false);
    const [stoppingBot, setStoppingBot] = useState(false);

    // Check for active automation runs when chat changes
    useEffect(() => {
        if (!chat?.leadId) {
            setHasActiveAutomation(false);
            return;
        }

        let cancelled = false;
        const checkAutomation = async () => {
            try {
                const runs = await getRunsForLead(chat.leadId);
                if (!cancelled) {
                    const activeRuns = runs.filter(r =>
                        ['running', 'waiting', 'paused'].includes((r.status || '').toLowerCase())
                    );
                    setHasActiveAutomation(activeRuns.length > 0);
                }
            } catch (err) {
                console.error('Error checking automation runs:', err);
            }
        };
        checkAutomation();
        return () => { cancelled = true; };
    }, [chat?.leadId]);

    // Stop Bot handler
    const handleStopBot = useCallback(async () => {
        if (!chat?.leadId || stoppingBot) return;
        setStoppingBot(true);
        try {
            const runs = await getRunsForLead(chat.leadId);
            const activeRuns = runs.filter(r =>
                ['running', 'waiting', 'paused'].includes((r.status || '').toLowerCase())
            );
            await Promise.all(activeRuns.map(run => stopRun(run.id, 'manual')));
            setHasActiveAutomation(false);
        } catch (err) {
            console.error('Error stopping automation:', err);
        } finally {
            setStoppingBot(false);
        }
    }, [chat?.leadId, stoppingBot]);

    const handleSendFirebaseMessage = async () => {
        if (!messageInput.trim() && pendingFiles.length === 0) return;
        if (!user?.uid || !chat?.id) {
            setError('User or conversation not initialized');
            return;
        }

        setSending(true);
        setError(null);

        try {
            // generate messageId before upload
            const clientMessageId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            // Upload pending files first
            let attachmentsMeta = [];
            if (pendingFiles.length > 0) {
                attachmentsMeta = await uploadAttachments({ files: pendingFiles, userId: user.uid, scope: 'conversation', conversationId: chat.id, messageId: clientMessageId });
            }

            // Send message through Firebase
            const result = await sendMessage(chat.id, {
                text: messageInput,
                channel: channel.toUpperCase(),
                isSender: true,
                subject: subject || undefined,
                attachments: attachmentsMeta.length ? attachmentsMeta : undefined,
                messageId: clientMessageId,
            });

            if (result.success) {
                setMessageInput('');
                setSubject('');
                setPendingFiles([]);
            } else {
                setError('Failed to send message');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            setError('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    if (showDetails) {
        return (
            <MobileDetailsPanel
                chat={leadData || chat}
                onBack={() => setShowDetails(false)}
                loading={loadingDetails}
            />
        );
    }

    return (
        <div className="flex flex-col h-screen bg-white">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-notion-border flex-shrink-0">
                <div className="flex items-center gap-2">
                    <button onClick={onBack} className="text-stone-600 hover:text-stone-800">
                        <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button onClick={() => setShowDetails(true)} className="text-left hover:opacity-80 transition">
                        <h2 className="text-sm font-semibold text-notion-text">{chat.name}</h2>
                        <p className="text-xs text-notion-text-light">Tap for details</p>
                    </button>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleStopBot}
                        disabled={!hasActiveAutomation || stoppingBot}
                        className={`px-3 py-1 text-xs font-medium rounded transition ${hasActiveAutomation
                            ? 'text-white bg-red-500 hover:bg-red-600'
                            : 'text-stone-400 bg-stone-100 cursor-not-allowed'
                            } disabled:opacity-50`}
                    >
                        {stoppingBot ? 'Stopping...' : 'Stop Bot'}
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-stone-500">Loading messages...</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <p className="text-sm text-stone-500">No messages yet. Start the conversation!</p>
                    </div>
                ) : (
                    messages.map((message, index) => {
                        const isBroadcast = message.isBroadcast === true || message.source === 'broadcast';
                        const isSystemCard = message.isSystem || isBroadcast;
                        const messageContent = getMessageContent(message);
                        const renderHtml = shouldRenderHtml(message, messageContent);
                        return (
                            <div key={index} className={`flex ${message.isSender || isSystemCard ? 'justify-end' : 'justify-start'}`}>
                                <div className={`flex flex-col ${message.isSender || isSystemCard ? 'items-end' : 'items-start'} max-w-[80%]`}>
                                    {isSystemCard && (
                                        <span className="flex items-center gap-1 text-[10px] font-semibold tracking-wide uppercase text-stone-400 mb-1">
                                            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                                            {isBroadcast ? 'BROADCAST' : 'AUTOMATED SYSTEM'}
                                        </span>
                                    )}
                                    {!isSystemCard && getMessageSourceLabel(message) && (
                                        <span className="text-[10px] font-medium text-blue-500 mb-1">{getMessageSourceLabel(message)}</span>
                                    )}
                                    <div
                                        className={`px-4 py-3 rounded-2xl ${isSystemCard
                                            ? 'bg-stone-100 text-stone-700'
                                            : message.isSender
                                                ? 'bg-stone-800 text-white'
                                                : 'bg-white border border-stone-200 text-stone-700'
                                            }`}
                                    >
                                        {/* For EMAIL channel, show subject in bold and body on separate line */}
                                        {message.channel === 'EMAIL' && message.metadata?.subject && (
                                            <>
                                                <p className="text-sm font-bold mb-2">{message.metadata.subject}</p>
                                                {renderHtml ? (
                                                    <div
                                                        className="text-sm leading-relaxed break-words email-html-body"
                                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }}
                                                    />
                                                ) : (
                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                                )}
                                            </>
                                        )}
                                        {/* For SMS or messages without subject */}
                                        {(message.channel !== 'EMAIL' || !message.metadata?.subject) && (
                                            renderHtml ? (
                                                <div
                                                    className="text-sm leading-relaxed break-words email-html-body"
                                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(messageContent) }}
                                                />
                                            ) : (
                                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{messageContent}</p>
                                            )
                                        )}
                                    </div>
                                    <p className="text-[10px] text-stone-400 mt-1.5">
                                        {formatMessageTime(message.timestamp)}
                                        {message.channel && message.channel !== 'Email' && ` • ${message.channel}`}
                                    </p>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-notion-border bg-white flex-shrink-0 pb-20">
                {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
                <div className="flex items-center gap-2 mb-3">
                    {['SMS', 'Email'].map((chan) => (
                        <button
                            key={chan}
                            disabled={sending}
                            className={`px-3 py-1 text-xs font-medium rounded transition ${channel === chan.toLowerCase()
                                ? 'bg-stone-800 text-white'
                                : 'bg-stone-100 text-stone-800 hover:bg-stone-200'
                                } disabled:opacity-50`}
                            onClick={() => setChannel(chan.toLowerCase())}
                        >
                            {chan}
                        </button>
                    ))}
                </div>

                <div className="mb-2">
                    {channel === 'email' && (
                        <>
                            <label className="text-xs text-notion-text-light">Subject</label>
                            <input
                                type="text"
                                placeholder="Enter email subject"
                                value={subject}
                                onChange={(e) => setSubject(e.target.value)}
                                disabled={sending}
                                className="w-full mt-1 mb-2 px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800 disabled:bg-stone-50"
                            />
                        </>
                    )}

                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Message..."
                            value={messageInput}
                            onChange={(e) => setMessageInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && !sending && handleSendFirebaseMessage()}
                            disabled={sending}
                            className="flex-1 px-3 py-2 text-sm border border-notion-border rounded focus:outline-none focus:ring-1 focus:ring-stone-800 disabled:bg-stone-50"
                        />
                        <label className="w-10 h-10 flex items-center justify-center bg-stone-100 text-stone-800 rounded cursor-pointer">
                            <input
                                type="file"
                                multiple
                                accept=".pdf,image/*,.doc,.docx,.txt"
                                className="hidden"
                                disabled={sending}
                                onChange={(e) => {
                                    try {
                                        const files = e.target.files;
                                        if (!files || files.length === 0) return;
                                        const val = filesToValidatedFiles(files);
                                        setPendingFiles(prev => [...prev, ...val]);
                                    } catch (err) {
                                        console.error('Attachment error:', err);
                                        setError(err.message || 'Attachment error');
                                    } finally {
                                        e.target.value = null;
                                    }
                                }}
                            />
                            📎
                        </label>
                        <button
                            onClick={handleSendFirebaseMessage}
                            disabled={sending || (!messageInput.trim() && pendingFiles.length === 0)}
                            title={sending ? `Sending ${channel.toUpperCase()}...` : `Send ${channel.toUpperCase()}`}
                            className="w-10 h-10 flex items-center justify-center bg-stone-800 text-white rounded-full hover:bg-stone-700 transition disabled:bg-stone-400 text-xs font-bold"
                        >
                            {sending ? '⋯' : '↑'}
                        </button>
                    </div>

                    {pendingFiles.length > 0 && (
                        <div className="mt-2 space-y-1">
                            {pendingFiles.map((a, i) => (
                                <div key={i} className="flex items-center justify-between text-sm bg-stone-50 p-2 rounded">
                                    <span className="truncate">{a.name} ({Math.round(a.size / 1024)} KB)</span>
                                    <button disabled={sending} onClick={() => setPendingFiles(prev => prev.filter((_, idx) => idx !== i))} className="text-xs text-red-500 ml-2">Remove</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileChatView;
